import { Button } from '@client/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@client/components/ui/card';
import { Separator } from '@client/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@client/components/ui/tabs';
import { Badge } from '@client/components/ui/badge';
import { 
  ArrowLeft,
  Edit,
  Trash2,
  Download,
  RefreshCw,
  Users,
  Calendar,
  Tag,
  Eye,
  Link as LinkIcon
} from 'lucide-react';
import { DataTable, TableColumn } from '@client/components/shared/DataTable';
import { StatCard } from './StatCard';
import { LinkedInSourceLink } from './LinkedInSourceLink';
import { ListMetadataDisplay } from './ListMetadataDisplay';
import type { List, Profile } from '@shared/types';

interface ListDetailProps {
  list: List;
  profiles: Profile[];
  isLoadingProfiles: boolean;
  onBack: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onExport: () => void;
  onViewAllProfiles: () => void;
  formatDateFull: (date: string) => string;
  getSourceBadge: (source: string) => React.ReactNode;
}

export function ListDetail({
  list,
  profiles,
  isLoadingProfiles,
  onBack,
  onEdit,
  onDelete,
  onExport,
  onViewAllProfiles,
  formatDateFull,
  getSourceBadge,
}: ListDetailProps) {
  const profileColumns: TableColumn<Profile>[] = [
    {
      key: 'name',
      header: 'Name',
      render: (profile) => (
        <div>
          <div className="font-medium">{profile.name || 'Unknown'}</div>
          {profile.title && (
            <div className="text-sm text-muted-foreground">{profile.title}</div>
          )}
        </div>
      ),
    },
    {
      key: 'company',
      header: 'Company',
      render: (profile) => profile.company || '-',
    },
    {
      key: 'location',
      header: 'Location',
      render: (profile) => profile.location || '-',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <header className="bg-white dark:bg-gray-900 shadow-sm border-b border-slate-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={onBack}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-200">
                  {list.name}
                </h1>
                {getSourceBadge(list.source)}
              </div>
              {list.description && (
                <p className="text-sm text-muted-foreground mt-1">
                  {list.description}
                </p>
              )}
              
              {/* LinkedIn Source Link */}
              {list.sourceUrl && (
                <div className="mt-2">
                  <LinkedInSourceLink 
                    list={list} 
                    variant="link"
                    showLabel={true}
                  />
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* LinkedIn Source Button (prominent) */}
            {list.sourceUrl && (
              <LinkedInSourceLink 
                list={list} 
                variant="button"
                showLabel={false}
              />
            )}
            
            <Button variant="outline" size="sm" onClick={onExport}>
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
            <Button variant="outline" size="sm" onClick={onEdit}>
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
            <Button variant="destructive" size="sm" onClick={onDelete}>
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="px-6 pb-6 space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <StatCard
            title="Total Profiles"
            value={list.profileCount || 0}
            icon={<Users className="h-4 w-4 text-muted-foreground" />}
            description={`${profiles.length} loaded in preview`}
          />
          <StatCard
            title="Created"
            value={new Date(list.createdAt).toLocaleDateString('de-DE')}
            icon={<Calendar className="h-4 w-4 text-muted-foreground" />}
            description={formatDateFull(list.createdAt)}
          />
          <StatCard
            title="Last Updated"
            value={new Date(list.updatedAt).toLocaleDateString('de-DE')}
            icon={<RefreshCw className="h-4 w-4 text-muted-foreground" />}
            description={formatDateFull(list.updatedAt)}
          />
          <StatCard
            title="Source"
            value={getSourceLabel(list.source)}
            icon={<Tag className="h-4 w-4 text-muted-foreground" />}
          />
        </div>

        {/* Tabs */}
        <Tabs defaultValue="profiles" className="space-y-4">
          <TabsList>
            <TabsTrigger value="profiles">Profiles</TabsTrigger>
            <TabsTrigger value="metadata">Metadata</TabsTrigger>
            <TabsTrigger value="query">Query Parameters</TabsTrigger>
            {list.sourceUrl && (
              <TabsTrigger value="source">LinkedIn Source</TabsTrigger>
            )}
          </TabsList>

          {/* Profiles Tab */}
          <TabsContent value="profiles" className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Profile Preview</CardTitle>
                    <CardDescription>
                      Showing first {profiles.length} profiles of {list.profileCount || 0} total
                    </CardDescription>
                  </div>
                  {(list.profileCount || 0) > 0 && (
                    <Button onClick={onViewAllProfiles}>
                      <Eye className="h-4 w-4 mr-2" />
                      View All Profiles
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <DataTable
                  data={profiles.slice(0, 20)}
                  columns={profileColumns}
                  loading={isLoadingProfiles}
                  getRowKey={(profile) => profile.id}
                  emptyState={{
                    title: 'No profiles in this list',
                    description: 'Profiles will appear here after scraping or manual addition',
                  }}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Metadata Tab */}
          <TabsContent value="metadata" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>List Metadata</CardTitle>
                <CardDescription>
                  Additional information and settings for this list
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">List ID</p>
                    <p className="text-sm">{list.id}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Created By</p>
                    <p className="text-sm">User #{list.createdBy || 1}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Query Hash</p>
                    <p className="text-sm font-mono text-xs break-all">
                      {list.queryHash || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Profile Count</p>
                    <p className="text-sm">{list.profileCount || 0}</p>
                  </div>
                </div>

                <Separator />

                {/* Structured Metadata */}
                {list.metadata && Object.keys(list.metadata).length > 0 && (
                  <ListMetadataDisplay list={list} variant="full" />
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Query Parameters Tab */}
          <TabsContent value="query" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>LinkedIn Query Parameters</CardTitle>
                <CardDescription>
                  Original search parameters used to create this list
                </CardDescription>
              </CardHeader>
              <CardContent>
                {list.queryParams && Object.keys(list.queryParams).length > 0 ? (
                  <div className="space-y-3">
                    {Object.entries(list.queryParams).map(([key, value]) => (
                      <div key={key} className="flex items-start gap-4 p-3 bg-muted rounded-md">
                        <div className="flex-1">
                          <p className="text-sm font-medium">{key}</p>
                          <p className="text-sm text-muted-foreground mt-1 break-all">
                            {String(value)}
                          </p>
                        </div>
                        <Badge variant="outline">{typeof value}</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <LinkIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
                    <p className="text-sm text-muted-foreground">
                      No query parameters available
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      This list was created manually or parameters were not stored
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* LinkedIn Source Tab (NEW) */}
          {list.sourceUrl && (
            <TabsContent value="source" className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>LinkedIn Source URL</CardTitle>
                  <CardDescription>
                    Original LinkedIn page where this list was scraped from
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Source URL Display */}
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-2">Source URL</p>
                    <div className="flex items-center gap-2 p-3 bg-muted rounded-md">
                      <code className="text-xs flex-1 break-all">
                        {list.sourceUrl}
                      </code>
                      <LinkedInSourceLink 
                        list={list} 
                        variant="button"
                        showLabel={false}
                      />
                    </div>
                  </div>

                  <Separator />

                  {/* Source Type */}
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-2">Source Type</p>
                    <Badge variant="outline" className="text-sm">
                      {getSourceLabel(list.source)}
                    </Badge>
                  </div>

                  {/* Quick Actions */}
                  <div className="flex gap-2 pt-2">
                    <LinkedInSourceLink 
                      list={list} 
                      variant="button"
                      showLabel={true}
                      className="flex-1"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        navigator.clipboard.writeText(list.sourceUrl || '');
                      }}
                    >
                      Copy URL
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
}

/**
 * Helper: Get readable source label
 */
function getSourceLabel(source: string): string {
  const labels: Record<string, string> = {
    'linkedin-search': 'People Search',
    'linkedin-group': 'Group Members',
    'linkedin-company': 'Company Employees',
    'linkedin-event': 'Event Attendees',
    'linkedin-single': 'Single Profile',
    'manual': 'Manual',
    'import': 'Import',
    'scraper': 'Scraper'
  };
  
  return labels[source] || source;
}